import { create } from 'zustand';
import { adminOrganizationManagementApi, adminUserApi } from '@api/api-config.ts';
import {
  AdminCreateOrganizationAddressRequest,
  AdminOrganizationDetailResponse,
  AdminUpdateOrganizationAddressRequest,
  AdminOrganizationNpBillingInfoResponse,
  AdminUpdateOrganizationRequest,
  OrganizationFeature,
} from '@api/generated/portal-be-admin';

interface AdminOrganizationDetailState {
  organization: AdminOrganizationDetailResponse | null;
  loading: boolean;
  error: string | null;
  /**
   * Chyba načtení z nákupního portálu. Drží se zvlášť od `error`, protože ten detail organizace
   * nahradí celostránkovou hláškou — a to, že firma v NP není, nesmí schovat detail, ze kterého
   * se dá zkusit jiné ID.
   */
  npBillingError: string | null;
  /** Chyba přidání adresy — ze stejného důvodu jako `npBillingError` nesmí schovat detail. */
  addressError: string | null;
  /** Chyba akce nad uživatelem (odebrání z firmy) — taky nesmí schovat detail. */
  userError: string | null;
}

interface AdminOrganizationDetailActions {
  loadOrganizationDetail: (organizationId: number) => Promise<void>;
  updateOrganization: (organizationId: number, request: AdminUpdateOrganizationRequest) => Promise<boolean>;
  /** `npOrganizationId` se posílá jen u firmy, ze které naše source_id do NP nevede (dodavatel z eCIS). */
  loadNpBillingInfo: (organizationId: number, npOrganizationId?: number) => Promise<AdminOrganizationNpBillingInfoResponse | null>;
  createOrganizationAddress: (organizationId: number, request: AdminCreateOrganizationAddressRequest) => Promise<boolean>;
  updateOrganizationAddress: (
    organizationId: number,
    addressId: number,
    request: AdminUpdateOrganizationAddressRequest,
  ) => Promise<boolean>;
  deleteOrganizationAddress: (organizationId: number, addressId: number) => Promise<boolean>;
  /**
   * Odebere uživateli přiřazení k této firmě (`organization_users`). Samotný účet zůstává —
   * ten se maže ze seznamu uživatelů.
   */
  deleteOrganizationUserAssignment: (organizationId: number, organizationUserId: number) => Promise<boolean>;
  updateOrganizationType: (organizationId: number, isSupplier: boolean, isClient: boolean) => Promise<boolean>;
  updateOrganizationCsvExport: (organizationId: number, enabled: boolean) => Promise<boolean>;
  /** `null` = vyřadit firmu ze skupiny. */
  updateOrganizationContractGroup: (organizationId: number, contractGroupId: number | null) => Promise<boolean>;
  updateOrganizationFeature: (organizationId: number, feature: OrganizationFeature, enabled: boolean) => Promise<boolean>;
  updateOrganizationIndexing: (organizationId: number, enabled: boolean, indexedPortalIds: number[]) => Promise<boolean>;
  clearError: () => void;
  clearNpBillingError: () => void;
  clearAddressError: () => void;
  clearUserError: () => void;
  reset: () => void;
}

export const useAdminOrganizationDetailStore = create<AdminOrganizationDetailState & AdminOrganizationDetailActions>((set, get) => ({
  organization: null,
  loading: false,
  error: null,
  npBillingError: null,
  addressError: null,
  userError: null,

  loadOrganizationDetail: async (organizationId: number) => {
    set({ loading: true, error: null });

    try {
      const response = await adminOrganizationManagementApi.getOrganizationDetail({ id: organizationId });
      set({
        organization: response.data,
        loading: false,
      });
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nepodařilo se načíst detail organizace';
      set({
        error: errorMessage,
        loading: false,
      });
    }
  },

  updateOrganization: async (organizationId: number, request: AdminUpdateOrganizationRequest) => {
    try {
      const response = await adminOrganizationManagementApi.updateOrganization({
        id: organizationId,
        adminUpdateOrganizationRequest: request,
      });
      set({ organization: response.data, error: null });
      return true;
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      set({ error: message || 'Nepodařilo se uložit organizaci' });
      return false;
    }
  },

  // Jen návrh do formuláře — do DB se údaje dostanou až uložením editace.
  loadNpBillingInfo: async (organizationId: number, npOrganizationId?: number) => {
    try {
      const response = await adminOrganizationManagementApi.getOrganizationNpBillingInfo({ id: organizationId, npOrganizationId });
      set({ npBillingError: null });
      return response.data;
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      set({ npBillingError: message || 'Nepodařilo se načíst údaje z nákupního portálu' });
      return null;
    }
  },

  // BE vrací celý detail, takže se nová adresa objeví ve výpisu bez dalšího načtení.
  createOrganizationAddress: async (organizationId: number, request: AdminCreateOrganizationAddressRequest) => {
    try {
      const response = await adminOrganizationManagementApi.createAdminOrganizationAddress({
        id: organizationId,
        adminCreateOrganizationAddressRequest: request,
      });
      set({ organization: response.data, addressError: null });
      return true;
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      set({ addressError: message || 'Nepodařilo se přidat adresu' });
      return false;
    }
  },

  updateOrganizationAddress: async (
    organizationId: number,
    addressId: number,
    request: AdminUpdateOrganizationAddressRequest,
  ) => {
    try {
      const response = await adminOrganizationManagementApi.updateAdminOrganizationAddress({
        id: organizationId,
        addressId,
        adminUpdateOrganizationAddressRequest: request,
      });
      set({ organization: response.data, addressError: null });
      return true;
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      set({ addressError: message || 'Nepodařilo se uložit adresu' });
      return false;
    }
  },

  deleteOrganizationAddress: async (organizationId: number, addressId: number) => {
    try {
      const response = await adminOrganizationManagementApi.deleteAdminOrganizationAddress({
        id: organizationId,
        addressId,
      });
      set({ organization: response.data, addressError: null });
      return true;
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      set({ addressError: message || 'Nepodařilo se smazat adresu' });
      return false;
    }
  },

  // Endpoint nevraci detail firmy, takze se seznam uzivatelu musi nacist znovu.
  deleteOrganizationUserAssignment: async (organizationId: number, organizationUserId: number) => {
    try {
      await adminUserApi.deleteOrganizationUser({ organizationUserId });
      set({ userError: null });
      await get().loadOrganizationDetail(organizationId);
      return true;
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      set({ userError: message || 'Nepodařilo se odebrat uživatele z firmy' });
      return false;
    }
  },

  updateOrganizationType: async (organizationId: number, isSupplier: boolean, isClient: boolean) => {
    try {
      const response = await adminOrganizationManagementApi.updateOrganizationType({
        id: organizationId,
        adminUpdateOrganizationTypeRequest: { isSupplier, isClient },
      });
      set({ organization: response.data });
      return true;
    } catch (err: any) {
      set({ error: err.response?.data?.message || 'Nepodařilo se aktualizovat typ organizace' });
      return false;
    }
  },

  // Přepínač CSV přílohy objednávky — BE ho pustí jen u organizace označené jako dodavatel.
  updateOrganizationCsvExport: async (organizationId: number, enabled: boolean) => {
    try {
      const response = await adminOrganizationManagementApi.updateOrganizationCsvExport({
        id: organizationId,
        adminUpdateOrganizationCsvExportRequest: { enabled },
      });
      set({ organization: response.data, error: null });
      return true;
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      set({ error: message || 'Nepodařilo se změnit nastavení CSV exportu' });
      return false;
    }
  },

  // Firma je nejvýš v jedné skupině — BE ji ze staré skupiny přesune sám.
  updateOrganizationContractGroup: async (organizationId: number, contractGroupId: number | null) => {
    try {
      const response = await adminOrganizationManagementApi.updateOrganizationContractGroup({
        id: organizationId,
        adminUpdateOrganizationContractGroupRequest: { contractGroupId: contractGroupId ?? undefined },
      });
      set({ organization: response.data, error: null });
      return true;
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      set({ error: message || 'Nepodařilo se změnit fakturační skupinu' });
      return false;
    }
  },

  updateOrganizationFeature: async (organizationId: number, feature: OrganizationFeature, enabled: boolean) => {
    try {
      // BE vrací celý detail, takže se rovnou promítne i změněná platnost přiřazení.
      const response = await adminOrganizationManagementApi.updateOrganizationFeature({
        id: organizationId,
        feature,
        adminUpdateOrganizationFeatureRequest: { enabled },
      });
      set({ organization: response.data });
      return true;
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      set({ error: message || 'Nepodařilo se změnit prémiovou funkci' });
      return false;
    }
  },

  // BE po uložení publikuje reindex firmy, takže se index srovná bez další akce admina.
  updateOrganizationIndexing: async (organizationId: number, enabled: boolean, indexedPortalIds: number[]) => {
    try {
      const response = await adminOrganizationManagementApi.updateOrganizationIndexing({
        id: organizationId,
        adminUpdateOrganizationIndexingRequest: { enabled, indexedPortalIds },
      });
      set({ organization: response.data, error: null });
      return true;
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      set({ error: message || 'Nepodařilo se uložit nastavení indexace' });
      return false;
    }
  },

  clearError: () => {
    set({ error: null });
  },

  clearNpBillingError: () => {
    set({ npBillingError: null });
  },

  clearAddressError: () => {
    set({ addressError: null });
  },

  clearUserError: () => {
    set({ userError: null });
  },

  reset: () => {
    set({
      organization: null,
      loading: false,
      error: null,
      npBillingError: null,
      addressError: null,
      userError: null,
    });
  },
}));
